/**
 * TicketDetail — thread view with public replies + internal notes (staff-only),
 * status/priority/assignee inline editors, resolve/reopen actions.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Send, AlertTriangle, Clock, Lock, Folder, Wand2, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import AttachmentComposer, { AttachmentList } from "@/components/AttachmentComposer";

const PRIORITY_LABEL = { urgent: "Urgent", high: "High", medium: "Medium", low: "Low" };

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isStaff = ["admin", "support", "sales", "marketing", "logistics", "b2b_admin"].includes(user?.role);
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [ticketFiles, setTicketFiles] = useState([]);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: c }, { data: fs }] = await Promise.all([
        api.get(`/crm/tickets/${id}`),
        api.get(`/crm/tickets/${id}/comments`),
        api.get(`/crm/tickets/${id}/files`).catch(() => ({ data: [] })),
      ]);
      setTicket(t);
      setComments(c);
      setTicketFiles(fs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ticket");
      nav("/app/crm/tickets");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isStaff) api.get("/crm/teammates").then((r) => setTeammates(r.data || [])).catch(() => {});
  }, [isStaff]);

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch(`/crm/tickets/${id}`, { [field]: value });
      setTicket(data);
      toast.success(`${field.replace("_", " ")} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const sendReply = async () => {
    if (!reply.trim() && replyFiles.length === 0) return;
    setSending(true);
    try {
      await api.post(`/crm/tickets/${id}/comments`, {
        body: reply,
        is_internal: isInternal,
        file_ids: replyFiles.map((f) => f.id),
      });
      setReply("");
      setReplyFiles([]);
      setIsInternal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally {
      setSending(false);
    }
  };

  const resolve = () => api.post(`/crm/tickets/${id}/resolve`).then(load).catch((e) => toast.error(e?.response?.data?.detail));
  const reopen = () => api.post(`/crm/tickets/${id}/reopen`).then(load).catch((e) => toast.error(e?.response?.data?.detail));
  const closeIt = () => api.post(`/crm/tickets/${id}/close`).then(load).catch((e) => toast.error(e?.response?.data?.detail));

  const escalate = async () => {
    const reason = window.prompt("Reason for escalation? (optional)");
    if (reason === null) return;
    try {
      await api.post(`/crm/tickets/${id}/escalate`, { reason });
      toast.success("Ticket escalated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Escalate failed"); }
  };

  const aiDraft = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/ai/crm-reply-draft", { ticket_id: id, tone: "empathetic" });
      setReply(data.draft || "");
      toast.success("AI draft loaded — review before sending");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setSending(false); }
  };

  if (loading || !ticket) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const canReopen = ticket.status === "resolved" || ticket.status === "closed";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-testid="ticket-detail-page">
      <div className="space-y-4 min-w-0">
        <button onClick={() => nav("/app/crm/tickets")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="ticket-back">
          <ArrowLeft className="w-4 h-4" /> Back to tickets
        </button>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">{ticket.ticket_number}</span>
            {ticket.sla_breached && <Badge variant="outline" className="bg-red-500/15 text-red-500 border-red-500/30"><AlertTriangle className="w-3 h-3 mr-1" /> SLA breached</Badge>}
          </div>
          <h1 className="mt-1 font-display text-3xl font-light" data-testid="ticket-subject-heading">{ticket.subject}</h1>
          <div className="text-sm text-muted-foreground mt-1">Raised by {ticket.requester_name || "—"} · {new Date(ticket.created_at).toLocaleString()}</div>
          {ticket.escalation_level && (
            <div className="mt-2">
              <Badge className="bg-rose-500 text-white" data-testid="ticket-escalation-badge">
                <ArrowUp className="w-3 h-3 mr-1" /> Escalated · {ticket.escalation_level}
              </Badge>
              {ticket.escalation_reason && <span className="ml-2 text-xs text-muted-foreground">"{ticket.escalation_reason}"</span>}
            </div>
          )}
        </div>

        <div className="border border-border rounded-lg p-4 bg-muted/30 whitespace-pre-wrap text-sm" data-testid="ticket-description-box">
          {ticket.description}
        </div>

        {/* Thread */}
        <div className="space-y-3">
          {comments.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">No replies yet.</div>
          ) : comments.map((c) => {
            const attached = (c.file_ids || []).map((fid) => ticketFiles.find((f) => f.id === fid)).filter(Boolean);
            return (
            <div
              key={c.id}
              className={`rounded-lg p-3 border ${c.is_internal ? "border-amber-500/30 bg-amber-500/5" : "border-border bg-card"}`}
              data-testid={`ticket-comment-${c.id}`}
            >
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{c.author_name || "—"}</span>
                <span>· {new Date(c.created_at).toLocaleString()}</span>
                {c.is_internal && <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30"><Lock className="w-3 h-3 mr-1" /> Internal</Badge>}
              </div>
              <div className="mt-1.5 text-sm whitespace-pre-wrap">{c.body}</div>
              {attached.length > 0 && <AttachmentList files={attached} testId={`ticket-comment-${c.id}-files`} />}
            </div>
          );})}
        </div>

        {/* Reply composer */}
        {ticket.status !== "closed" && (
          <div className="border border-border rounded-lg p-3 space-y-2" data-testid="ticket-reply-box">
            <Textarea
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={isInternal ? "Internal note — visible to staff only" : "Reply to the requester"}
              data-testid="ticket-reply-input"
            />
            <AttachmentComposer
              files={replyFiles}
              onChange={setReplyFiles}
              compact
              testId="ticket-attach"
            />
            <div className="flex items-center justify-between">
              {isStaff && (
                <div className="flex items-center gap-2">
                  <Switch checked={isInternal} onCheckedChange={setIsInternal} data-testid="ticket-internal-toggle" />
                  <Label className="text-xs">Internal note</Label>
                </div>
              )}
              <div className="ml-auto flex gap-2 flex-wrap">
                {isStaff && (
                  <>
                    <Button variant="outline" size="sm" onClick={aiDraft} disabled={sending} data-testid="ticket-ai-draft-btn">
                      <Wand2 className="w-3.5 h-3.5 mr-1" /> AI draft
                    </Button>
                    {ticket.status !== "closed" && ticket.escalation_level !== "L4" && (
                      <Button variant="outline" size="sm" onClick={escalate} data-testid="ticket-escalate-btn">
                        <ArrowUp className="w-3.5 h-3.5 mr-1" /> Escalate
                      </Button>
                    )}
                  </>
                )}
                {ticket.status !== "resolved" && ticket.status !== "closed" && isStaff && (
                  <Button variant="outline" size="sm" onClick={resolve} data-testid="ticket-resolve-btn">Mark resolved</Button>
                )}
                {canReopen && (
                  <Button variant="outline" size="sm" onClick={reopen} data-testid="ticket-reopen-btn">Reopen</Button>
                )}
                {ticket.status === "resolved" && isStaff && (
                  <Button variant="outline" size="sm" onClick={closeIt} data-testid="ticket-close-btn">Close</Button>
                )}
                <Button size="sm" onClick={sendReply} disabled={sending || (!reply.trim() && replyFiles.length === 0)} data-testid="ticket-send-btn">
                  <Send className="w-4 h-4 mr-1.5" /> Send
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <aside className="space-y-4">
        <div className="border border-border rounded-lg p-4 space-y-3">
          <SidebarRow label="Status">
            {isStaff ? (
              <Select value={ticket.status} onValueChange={(v) => patch("status", v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="reopened">Reopened</SelectItem>
                </SelectContent>
              </Select>
            ) : <span className="text-sm">{ticket.status}</span>}
          </SidebarRow>

          <SidebarRow label="Priority">
            {isStaff ? (
              <Select value={ticket.priority} onValueChange={(v) => patch("priority", v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <span className="text-sm">{PRIORITY_LABEL[ticket.priority]}</span>}
          </SidebarRow>

          <SidebarRow label="Assignee">
            {isStaff ? (
              <Select value={ticket.assigned_to || "unassigned"} onValueChange={(v) => patch("assigned_to", v === "unassigned" ? null : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="ticket-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teammates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <span className="text-sm">{teammates.find((t) => t.id === ticket.assigned_to)?.name || "Unassigned"}</span>}
          </SidebarRow>

          <SidebarRow label="Department">
            <span className="text-sm">{ticket.department || "—"}</span>
          </SidebarRow>

          <SidebarRow label="SLA">
            <span className={`text-sm flex items-center gap-1 ${ticket.sla_breached ? "text-red-500" : ""}`}>
              <Clock className="w-3 h-3" /> Due {new Date(ticket.sla_due_at).toLocaleString()}
            </span>
          </SidebarRow>

          {ticket.reopened_count > 0 && (
            <SidebarRow label="Reopened"><span className="text-sm">{ticket.reopened_count}×</span></SidebarRow>
          )}
        </div>

        {/* Files sidebar — aggregated attachments across all thread comments */}
        <div className="border border-border rounded-lg p-4" data-testid="ticket-files-sidebar">
          <div className="flex items-center gap-2 mb-3">
            <Folder className="w-4 h-4 text-primary" />
            <div className="text-sm font-medium">Files</div>
            <span className="text-xs text-muted-foreground ml-auto">{ticketFiles.length}</span>
          </div>
          {ticketFiles.length === 0 ? (
            <div className="text-xs text-muted-foreground">No attachments yet. Use the paperclip in the reply box to add files.</div>
          ) : (
            <div className="space-y-2">
              {ticketFiles.map((f) => {
                const isImage = f.content_type?.startsWith("image/");
                return (
                  <a
                    key={f.id}
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    download={f.original_filename}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors"
                    data-testid={`ticket-file-${f.id}`}
                  >
                    {isImage && f.thumb_url ? (
                      <img src={f.thumb_url} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                        <Folder className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs truncate">{f.original_filename}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {f.author_name} · {new Date(f.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function SidebarRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}
